import React, { useEffect, useRef, useState } from 'react';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import { UploadImageIcon } from '../../../icons/upload-image';
import {
  AddNewPropertyForm,
  AddNewPropertyFormContainer,
  ButtonTitle,
  ButtonsContainer,
  CancelButton,
  DashboardContent,
  ImageUploadContainer,
  InputContainer,
  StyledImage,
  Title,
} from './dashboard-buildings.styled';
import { useNavigate } from 'react-router-dom';
import { useRedirectOnBack } from '../../../hooks/use-redirect-on-back';
import { useAuth } from '../../../../context/auth-provider';
import { User } from '../../../../interfaces/user-interfaces';
import { createItem, fetchAllItems } from '../../../requests/requests';

interface DashboardViewBuildingByIdProps {
  building: BuildingData | undefined;
}

/**
 * One stored value: label in bold, value beside it. An empty column renders an em dash
 * rather than a blank gap, so a genuinely empty field does not read as a rendering failure,
 * and a supervision-created building's "TBD - NEEDS CUSTOMER INPUT" shows as the placeholder
 * it is.
 */
const DetailRow: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      gap: 8,
      alignItems: 'baseline',
      flexWrap: 'wrap',
      marginBottom: 12,
    }}
  >
    <Text fontSize={14} fontWeight={700} mb={0}>
      {label}:
    </Text>
    <Text fontSize={14} mb={0} color={colors.grey_dark}>
      {typeof value === 'string' && value.trim().length > 0 ? value : '—'}
    </Text>
  </div>
);

/**
 * A building's details, read-only. Replaced the Edit Property form on 2026-09-15.
 *
 * Every column of properties is owned and written by supervision-frontend -- the building
 * itself, its contacts, and both report distribution lists, including this app's own
 * recipient list (properties.email), which the Building form's Emails tab now manages.
 * Concierge reads. concierge-backend has no write route to properties at all, so this is
 * enforced on the server, not only by the absence of inputs here.
 *
 * Earlier the same day this screen still let you edit the email list, which left that one
 * column with two writers -- concierge here, and supervision's Emails tab -- where the last
 * save silently won. A single owner removes that rather than managing it.
 */
export const DashboardViewBuildingById: React.FC<
  DashboardViewBuildingByIdProps
> = ({ building }) => {
  const navigate = useNavigate();
  const { user }: { user: User | null | undefined } = useAuth();

  // Reached with no building selected (a refresh, or typing the URL): nothing to show.
  useEffect(() => {
    if (!building || !building.property_name) {
      navigate('dashboard/buildings');
    }
  }, [building]);

  useRedirectOnBack('/dashboard');

  // Same expiring-URL recovery as the Buildings list, with the same 5-minute throttle.
  // `building` is handed over once by the list, so a fresh URL is kept here instead.
  const [freshImageSrc, setFreshImageSrc] = useState<string | null>(null);
  const lastImageRefetchAt = useRef(0);

  const imageSrc =
    freshImageSrc ??
    (typeof building?.property_image === 'string'
      ? building.property_image
      : null);

  const handleImageError = async () => {
    if (!user || !building) return;
    if (Date.now() - lastImageRefetchAt.current < 5 * 60 * 1000) return;
    lastImageRefetchAt.current = Date.now();

    const buildings = (
      user.role === 'admin'
        ? await fetchAllItems('dashboard/buildings')
        : await createItem('dashboard/buildings', user)
    ) as BuildingData[];
    const fresh = buildings.find(b => b.id === building.id)?.property_image;
    if (typeof fresh === 'string' && fresh !== imageSrc)
      setFreshImageSrc(fresh);
  };
  const emails = (building?.email ?? []).filter(
    email => typeof email === 'string' && email.trim().length > 0
  );

  return (
    <DashboardContent id="dashboard-view-building" isAddNewBuilding={true}>
      <Title>
        <Text mt={16} ml={16} mb={0} fontSize={28} fontWeight={700}>
          Property details
        </Text>
        <Text mt={6} ml={16} mb={0} fontSize={14} color={`${colors.grey}`}>
          Buildings are managed in Supervision
        </Text>
      </Title>

      <AddNewPropertyFormContainer>
        <AddNewPropertyForm>
          <InputContainer isImageUpload={true}>
            <ImageUploadContainer>
              {imageSrc ? (
                <StyledImage
                  src={imageSrc}
                  alt="Property Image"
                  isAddNewPropertyForm
                  onError={handleImageError}
                />
              ) : (
                <UploadImageIcon />
              )}
            </ImageUploadContainer>
          </InputContainer>

          <DetailRow label="Property name" value={building?.property_name} />
          <DetailRow label="Contact person" value={building?.contact_person} />
          <DetailRow label="Position" value={building?.position} />
          {/* Phone belongs to the contact person, not the building -- see the note in
              supervision-frontend's BuildingInfoSection -- so it is listed with them. */}
          <DetailRow label="Phone" value={building?.phone_number} />
          <DetailRow label="Country" value={building?.country} />
          <DetailRow label="Address" value={building?.address} />

          {/* A flex column, not a plain block with marginTop, so this spaces itself the way a
              DetailRow does. Text is a styled <p> whose margin-top is only emitted when an mt
              prop is passed; without one the browser's default 1em <p> margin applies. Inside
              DetailRow's flex container that margin is contained and adds to the gap, but in a
              plain block it collapses through the wrapper -- which made the space above this
              block roughly half that of every other field. */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text fontSize={14} fontWeight={700} mb={8}>
              Report recipients:
            </Text>
            {/* Indented so the list reads as this field's contents rather than another run
                of fields. line-height lives here rather than on Text: Text declares a
                lineHeight prop but never emits it, so passing one would silently do nothing.
                Cascading from this wrapper reaches the <p> children. No per-email margin --
                comfortable leading does the work, and a long list stays compact.
                1.7 rather than ~1.45: the default is line-height normal (~1.2), so at 14px
                the old rendering was ~16.8px plus a 4px margin, about 20.8px per line. A
                1.45 leading with no margin lands at ~20.3px -- visually identical. 1.7 gives
                ~23.8px, which is the first value that actually reads as more open. */}
            <div style={{ paddingLeft: 10, lineHeight: 1.7 }}>
              {emails.length > 0 ? (
                emails.map(email => (
                  <Text
                    key={email}
                    fontSize={14}
                    mt={0}
                    mb={0}
                    color={colors.grey_dark}
                  >
                    {email}
                  </Text>
                ))
              ) : (
                <Text fontSize={14} mt={0} mb={0} color={colors.grey_dark}>
                  —
                </Text>
              )}
            </div>
          </div>
        </AddNewPropertyForm>
      </AddNewPropertyFormContainer>

      <ButtonsContainer>
        <CancelButton onClick={() => navigate('buildings')}>
          <ButtonTitle>Back</ButtonTitle>
        </CancelButton>
      </ButtonsContainer>
    </DashboardContent>
  );
};
